import api from '../../../api';
import { ResponseSuccess } from '../../../shared/types';
import {
  AddTagsToLeadsPayload,
  BulkActionPayload,
  BulkActionRemoveTagsPayload,
  BulkAddToProspectSavedDto,
  BulkAddToSequenceSavedDto,
  BulkLeadExportSavedDto,
  BulkExportCompanyDto,
  CompanyTagActionPayload,
  CompanyBulkTagActionPayload,
  ExportCompanyDto,
  RemoveTagsToLeadsPayload,
  FetchAISearchResultPayload,
  LeadFinderListTab,
  CreateListPayload,
  SavedFilterTab,
  LeadFinderSecondaryTab,
} from '../types';

export const getLeadsV2 = async (params, leadPayload) => {
  const queryParams = new URLSearchParams(window.location?.search);
  if (queryParams?.get('purgeCache') === '1') {
    leadPayload.purgeCache = true;
  }
  return api.post<ResponseSuccess>(`/lead-finder/v2/leads/search?${params}`, {
    ...leadPayload,
  });
};

export const getLeadsV2Count = async (leadPayload) => {
  const queryParams = new URLSearchParams(window.location?.search);
  if (queryParams?.get('purgeCache') === '1') {
    leadPayload.purgeCache = true;
  }
  leadPayload.tab = 'total';
  return api.post<ResponseSuccess>(`/lead-finder/v2/leads/counts`, {
    ...leadPayload,
  });
};

export const getSavedLeadsV2Count = async (leadPayload) => {
  const queryParams = new URLSearchParams(window.location?.search);
  if (queryParams?.get('purgeCache') === '1') {
    leadPayload.purgeCache = true;
  }
  leadPayload.tab = 'saved';
  const { listIds, peopleList, ...restPayload } = leadPayload;

  const mappedPayload: any = { ...restPayload };
  if (peopleList) {
    mappedPayload.peopleList = peopleList;
  } else if (listIds) {
    mappedPayload.peopleList = listIds;
  }

  return api.post<ResponseSuccess>(`/lead-finder/v2/leads/counts`, {
    ...mappedPayload,
  });
};

export const fetchAISearchResult = (payload: FetchAISearchResultPayload) =>
  api.post<ResponseSuccess>(`/lead-finder/v2/leads/ai-search`, {
    ...payload,
  });

export const fetchLeadStatusPoll = async (leadPayload) =>
  api.post<ResponseSuccess>(`/lead-finder/status`, {
    ...leadPayload,
  });

export const getSavedLeads = async (leadPayload) => {
  const keyword = leadPayload?.search;
  // Support both `tags` and legacy/alternate `tagIds` keys coming from callers
  const tags = leadPayload?.tags ?? leadPayload?.tagIds;
  const createdDate = leadPayload?.createdDate;
  let listIds: any = leadPayload?.listIds;
  if (!listIds) {
    if (leadPayload?.peopleList) {
      listIds = leadPayload.peopleList;
    } else if (leadPayload?.companyList) {
      listIds = leadPayload.companyList;
    }
  }
  let queryParams = `start=${leadPayload?.start}&take=${leadPayload?.take}`;
  const keywordValue = Array.isArray(keyword)
    ? keyword.filter(Boolean).join(',')
    : keyword;
  if (keywordValue) {
    queryParams += `&search=${keywordValue}`;
  }
  if (tags) {
    queryParams += `&tags=${tags}`;
  }
  if (createdDate) {
    queryParams += `&createdDate=${createdDate}`;
  }
  if (listIds) {
    let listIdsArray: number[] = [];

    if (Array.isArray(listIds)) {
      listIdsArray = listIds;
    } else if (listIds.includes && Array.isArray(listIds.includes)) {
      listIdsArray = listIds.includes;
    }

    if (listIdsArray.length > 0) {
      const listIdsValue = listIdsArray
        .filter((id) => !Number.isNaN(id) && id > 0)
        .join(',');
      if (listIdsValue) {
        queryParams += `&listIds=${listIdsValue}`;
      }
    }
  }
  if (leadPayload?.excludeProspects) {
    queryParams += `&excludeProspects=true`;
  }
  return api.get<ResponseSuccess>(`/lead-finder/v2/leads?${queryParams}`);
};

export const editSavedFilterAPI = async (filterId, apiParam) =>
  api.patch(
    // eslint-disable-next-line no-underscore-dangle
    `/lead-finder/v2/leads/filters/${filterId}`,
    apiParam,
  );

export const deleteSavedFilterAPI = async (filterId) =>
  api.delete(
    // eslint-disable-next-line no-underscore-dangle
    `/lead-finder/v2/leads/filters/${filterId}`,
  );

export const addSavedFilterAPI = async (apiParam) =>
  api.post(`/lead-finder/v2/leads/filters`, apiParam);

export const getSavedFilterListAPI = async (
  tab: SavedFilterTab = SavedFilterTab.PEOPLE,
) => api.get(`/lead-finder/v2/leads/filters?tab=${tab}`);

export const getFilterSuggestionAPI = async ({ type, search }) =>
  api.get(`/lead-finder/filter-suggestion/${type}?search=${search}`);

export const getV2FilterSuggestionAPI = async ({ type, search, paramType }) =>
  api.get(
    `/lead-finder/filter-suggestion/${type}?version=v2&search=${search}${
      paramType ? `&type=${paramType}` : ''
    }`,
  );

export const leadAddToProspectAPI = async (leadId) =>
  api.get(`/lead-finder/v2/leads/add-to-prospect/${leadId}`);

export const leadAddToSequenceAPI = async (payload) =>
  api.post(`/lead-finder/v2/leads/add-to-sequence`, payload);

export const bulkRevealLeadAPI = async (payload) =>
  api.post(`/lead-finder/v2/leads/bulk-actions/reveal`, payload);

export const bulkAddToProspectLeadAPI = async (payload) =>
  api.post(`/lead-finder/v2/leads/bulk-actions/add-to-prospect`, payload);

export const bulkAddToSequenceLeadAPI = async (payload) =>
  api.post(`/lead-finder/v2/leads/bulk-actions/add-to-sequence`, payload);

export const bulkSavedAddToSequenceLeadAPI = async (
  payload: BulkAddToSequenceSavedDto,
) => {
  const filteredPayload: BulkAddToSequenceSavedDto = {
    ...payload,
    leadsFilter: {
      ...(payload.leadsFilter?.tags?.length > 0 && {
        tags: payload.leadsFilter?.tags,
      }),
      ...(payload.leadsFilter?.search?.length > 0 && {
        search: payload.leadsFilter?.search,
      }),
      ...(payload.leadsFilter?.createdDate?.length > 0 && {
        createdDate: payload.leadsFilter?.createdDate,
      }),
      ...(payload.leadsFilter?.excludeProspects && {
        excludeProspects: payload.leadsFilter.excludeProspects,
      }),
    },
  };
  return api.post(
    `/lead-finder/v2/leads/bulk-actions/saved-add-to-sequence`,
    filteredPayload,
  );
};

export const bulkSavedAddToProspectLeadAPI = async (
  payload: BulkAddToProspectSavedDto,
) => {
  const filteredPayload: BulkAddToProspectSavedDto = {
    ...payload,
    leadsFilter: {
      ...(payload.leadsFilter?.tags?.length > 0 && {
        tags: payload.leadsFilter?.tags,
      }),
      ...(payload.leadsFilter?.search?.length > 0 && {
        search: payload.leadsFilter?.search,
      }),
      ...(payload.leadsFilter?.createdDate?.length > 0 && {
        createdDate: payload.leadsFilter?.createdDate,
      }),
      ...(payload.leadsFilter?.excludeProspects && {
        excludeProspects: payload.leadsFilter.excludeProspects,
      }),
    },
  };
  return api.post(
    `/lead-finder/v2/leads/bulk-actions/saved-add-to-prospect`,
    filteredPayload,
  );
};

export const bulkExportLeadAPI = async (payload) =>
  api.post(`/lead-finder/v2/leads/export`, payload);

export const exportAllLeadsAPI = async (payload: BulkLeadExportSavedDto) => {
  const rawListIds = payload.leadsFilter?.listIds;
  let listIdsToSend;

  if (Array.isArray(rawListIds)) {
    listIdsToSend = rawListIds;
  }

  const filteredPayload: BulkAddToProspectSavedDto = {
    ...payload,
    leadsFilter: {
      ...(payload.leadsFilter?.tags?.length > 0 && {
        tags: payload.leadsFilter?.tags,
      }),
      ...(payload.leadsFilter?.search?.length > 0 && {
        search: payload.leadsFilter?.search,
      }),
      ...(payload.leadsFilter?.createdDate?.length > 0 && {
        createdDate: payload.leadsFilter?.createdDate,
      }),
      ...(listIdsToSend !== undefined && {
        listIds: listIdsToSend,
      }),
      ...(payload.leadsFilter?.excludeProspects && {
        excludeProspects: payload.leadsFilter.excludeProspects,
      }),
    },
  };

  return api.post(`/lead-finder/v2/leads/export-async`, filteredPayload);
};

export const getTagsToLeads = async () =>
  api.get<ResponseSuccess>(`/lead-finder/v2/tags`);

export const addTagsToLeads = async ({
  tagIds,
  leads,
  newTags,
}: AddTagsToLeadsPayload) =>
  api.post<ResponseSuccess>('/lead-finder/v2/tag/assign', {
    tagIds,
    leads,
    newTags,
  });

export const removeTagsToLeads = async ({
  tagIds,
  leads,
}: RemoveTagsToLeadsPayload) =>
  api.post<ResponseSuccess>('/lead-finder/v2/tag/un-assign', {
    tagIds,
    leads,
  });

export const addTagsToBulkLeads = async (payload: BulkActionPayload) => {
  const filteredPayload: BulkActionPayload = {
    ...payload,
    leadsFilter: {
      ...(payload.leadsFilter?.tags?.length > 0 && {
        tags: payload.leadsFilter?.tags,
      }),
      ...(payload.leadsFilter?.search?.length > 0 && {
        search: payload.leadsFilter?.search,
      }),
      ...(payload.leadsFilter?.createdDate?.length > 0 && {
        createdDate: payload.leadsFilter?.createdDate,
      }),
      ...(payload.leadsFilter?.excludeProspects && {
        excludeProspects: payload.leadsFilter.excludeProspects,
      }),
    },
  };
  return api.post<ResponseSuccess>(
    '/lead-finder/v2/tag/bulk-assign',
    filteredPayload,
  );
};

export const removeTagsFromBulkLeads = async (
  payload: BulkActionRemoveTagsPayload,
) => {
  const filteredPayload: BulkActionRemoveTagsPayload = {
    ...payload,
    leadsFilter: {
      ...(payload.leadsFilter?.tags?.length > 0 && {
        tags: payload.leadsFilter?.tags,
      }),
      ...(payload.leadsFilter?.search?.length > 0 && {
        search: payload.leadsFilter?.search,
      }),
      ...(payload.leadsFilter?.createdDate?.length > 0 && {
        createdDate: payload.leadsFilter?.createdDate,
      }),
      ...(payload.leadsFilter?.excludeProspects && {
        excludeProspects: payload.leadsFilter.excludeProspects,
      }),
    },
  };
  return api.post<ResponseSuccess>(
    '/lead-finder/v2/tag/bulk-un-assign',
    filteredPayload,
  );
};

// ========================
// Company API Functions
// ========================

// Flag to use mock data (set based on feature flag or env)
// Set to true while backend API is not ready
// const USE_MOCK_COMPANY_API = true;

export const getCompaniesV2 = async (params: string, companyPayload: any) => {
  const queryParams = new URLSearchParams(window.location?.search);
  if (queryParams?.get('purgeCache') === '1') {
    companyPayload.purgeCache = true;
  }
  return api.post<ResponseSuccess>(
    `/lead-finder/v2/companies/search?${params}`,
    {
      ...companyPayload,
    },
  );
};

export const getCompaniesV2Count = async (companyPayload: any) => {
  const queryParams = new URLSearchParams(window.location?.search);
  if (queryParams?.get('purgeCache') === '1') {
    companyPayload.purgeCache = true;
  }
  companyPayload.tab = 'total';
  return api.post<ResponseSuccess>(`/lead-finder/v2/companies/counts`, {
    ...companyPayload,
  });
};

export const getSavedCompaniesV2Count = async (companyPayload: any) => {
  const queryParams = new URLSearchParams(window.location?.search);
  if (queryParams?.get('purgeCache') === '1') {
    companyPayload.purgeCache = true;
  }
  companyPayload.tab = 'saved';
  const { tagIds, listIds, tags, companyList, ...restPayload } = companyPayload;

  const mappedPayload: any = { ...restPayload };
  if (Array.isArray(tags)) {
    mappedPayload.tags = tags;
  } else if (Array.isArray(tagIds)) {
    mappedPayload.tags = tagIds;
  }

  if (companyList) {
    mappedPayload.companyList = companyList;
  } else if (listIds) {
    mappedPayload.companyList = listIds;
  }
  // If URL contains jobId, include it in the mapped payload.
  try {
    const urlParams = new URLSearchParams(window.location?.search);
    const jobIdParam = urlParams.get('jobId');
    const hasOnlyPagination =
      !tagIds &&
      !tags &&
      !listIds &&
      !companyList &&
      !restPayload?.search &&
      !restPayload?.createdDate &&
      !restPayload?.startDate &&
      !restPayload?.endDate;

    if (jobIdParam && hasOnlyPagination) {
      const jobIdNum = Number(jobIdParam);
      mappedPayload.jobId = Number.isNaN(jobIdNum) ? jobIdParam : jobIdNum;
    } else if (jobIdParam && !hasOnlyPagination) {
      // Remove jobId from URL when other filters are applied
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('jobId');
        window.history.replaceState(
          {},
          '',
          url.pathname + (url.search ? `?${url.searchParams.toString()}` : ''),
        );
      } catch (_e) {
        // ignore URL manipulation errors
      }
    }
  } catch (e) {
    // ignore and continue without jobId
  }

  return api.post<ResponseSuccess>(`/lead-finder/v2/companies/counts`, {
    ...mappedPayload,
  });
};

export const getSavedCompanies = async (companyPayload: {
  start?: number;
  take?: number;
  search?: string;
  tagIds?: number[];
  createdDate?: string;
  listIds?: {
    includes?: number[];
    excludes?: number[];
  };
  purgeCache?: boolean;
}) => {
  const queryParams = new URLSearchParams(window.location?.search);
  if (queryParams?.get('purgeCache') === '1') {
    companyPayload.purgeCache = true;
  }

  const payload: any = {
    start: companyPayload?.start ?? 1,
    take: companyPayload?.take ?? 25,
  };

  if (companyPayload?.search) {
    payload.search = companyPayload.search;
  }

  if (companyPayload?.tagIds && companyPayload.tagIds.length > 0) {
    payload.tagIds = companyPayload.tagIds;
  }

  if (companyPayload?.createdDate) {
    payload.createdDate = companyPayload.createdDate;
  }

  // If URL contains jobId, include it in the API payload so callers don't need to pass it explicitly.
  try {
    const urlParams = new URLSearchParams(window.location?.search);
    const jobIdParam = urlParams.get('jobId');
    // Consider jobId only when payload contains no other filters besides pagination/purgeCache
    const hasOnlyPagination =
      !companyPayload?.search &&
      !companyPayload?.tagIds &&
      !companyPayload?.createdDate &&
      !companyPayload?.listIds;

    if (jobIdParam && hasOnlyPagination) {
      const jobIdNum = Number(jobIdParam);
      payload.jobId = Number.isNaN(jobIdNum) ? jobIdParam : jobIdNum;
    } else if (jobIdParam && !hasOnlyPagination) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('jobId');
        window.history.replaceState(
          {},
          '',
          url.pathname + (url.search ? `?${url.searchParams.toString()}` : ''),
        );
      } catch (_e) {
        // ignore
      }
    }
  } catch (e) {
    // ignore URL parsing errors and continue without jobId
  }

  if (companyPayload?.listIds) {
    const listIds: any = {};
    if (
      companyPayload.listIds.includes &&
      companyPayload.listIds.includes.length > 0
    ) {
      listIds.includes = companyPayload.listIds.includes;
    }
    if (
      companyPayload.listIds.excludes &&
      companyPayload.listIds.excludes.length > 0
    ) {
      listIds.excludes = companyPayload.listIds.excludes;
    }
    if (Object.keys(listIds).length > 0) {
      payload.listIds = listIds;
    }
  }

  return api.post<ResponseSuccess>(`/lead-finder/v2/companies`, payload);
};

export const saveCompanyAPI = async (companyId: number) =>
  api.post<ResponseSuccess>(`/lead-finder/v2/companies/${companyId}/save`);

export const bulkSaveCompaniesAPI = async (companyIds: number[]) =>
  api.post<ResponseSuccess>(`/lead-finder/v2/companies/bulk-save`, {
    companyIds,
  });

// Deduplicate concurrent requests for the same tab to avoid duplicate network calls
const inflightCompanyListsRequests: Map<
  string | number,
  Promise<ResponseSuccess>
> = new Map();

export const getCompanyListsAPI = async (tab: LeadFinderListTab) => {
  const key = String(tab);
  const existing = inflightCompanyListsRequests.get(key);
  if (existing) {
    return existing;
  }

  const promise = api
    .get<ResponseSuccess>(`/lead-finder/v2/lists/${tab}`)
    .finally(() => {
      // Remove the inflight promise when it's settled so future calls re-fetch
      inflightCompanyListsRequests.delete(key);
    });

  inflightCompanyListsRequests.set(key, promise);
  return promise;
};

export const createListAPI = async (payload: CreateListPayload) =>
  api.post<ResponseSuccess>(`/lead-finder/v2/lists/create-list`, payload);

export const updateListAPI = async (
  listId: number,
  payload: CreateListPayload,
) => api.patch<ResponseSuccess>(`/lead-finder/v2/lists/${listId}`, payload);

export const deleteListAPI = async (listId: number) =>
  api.delete<ResponseSuccess>(`/lead-finder/v2/lists/${listId}`);

export const addToListsActionAPI = async (payload: {
  actionType: string;
  tab: number;
  subTab: string;
  resourceIds: number[];
  listIds?: number[];
  name?: string;
  visibility?: string;
  teamIds?: number[];
  shouldEnrich?: boolean;
}) => api.post<ResponseSuccess>(`/lead-finder/v2/lists/action`, payload);

export const addToListsBulkActionAPI = async (payload: {
  actionType: string;
  tab: number;
  subTab: string;
  listIds?: number[];
  name?: string;
  visibility?: string;
  teamIds?: number[];
  searchFilters: any;
  shouldEnrich?: boolean;
}) => api.post<ResponseSuccess>(`/lead-finder/v2/lists/bulk-action`, payload);

export const addToListsBulkActionSavedAPI = async (payload: {
  actionType: string;
  tab: number;
  listIds?: number[];
  name?: string;
  visibility?: string;
  teamIds?: number[];
  searchFilters: any;
  shouldEnrich?: boolean;
}) =>
  api.post<ResponseSuccess>(`/lead-finder/v2/lists/bulk-action-saved`, payload);

export const revealCompaniesAPI = async (payload: {
  shouldEnrich: boolean;
  totalCompanies: number;
  filters?: any;
  companyIds?: number[];
  listIds?: number[];
  newLists?: Array<{
    name: string;
    visibility: string;
    teamIds?: number[];
  }>;
}) => api.post<ResponseSuccess>(`/lead-finder/v2/companies/reveal`, payload);

export const exportAllCompaniesAPI = async (payload: BulkExportCompanyDto) => {
  const listIds = {
    ...(payload.companiesFilter?.listIds?.includes?.length > 0 && {
      includes: payload.companiesFilter?.listIds?.includes,
    }),
    ...(payload.companiesFilter?.listIds?.excludes?.length > 0 && {
      excludes: payload.companiesFilter?.listIds?.excludes,
    }),
  };
  const filteredPayload: BulkExportCompanyDto = {
    ...payload,
    companiesFilter: {
      ...(payload.companiesFilter?.tags?.length > 0 && {
        tags: payload.companiesFilter.tags,
      }),
      ...(payload.companiesFilter?.search &&
        payload.companiesFilter.search.length > 0 && {
          search: payload.companiesFilter.search,
        }),
      ...(payload.companiesFilter?.createdDate?.length > 0 && {
        createdDate: payload.companiesFilter.createdDate,
      }),
      ...(Object.keys(listIds).length > 0 && {
        listIds,
      }),
    },
  };

  return api.post(`/lead-finder/v2/companies/export-async`, filteredPayload);
};

export const exportSelectedCompaniesAPI = async (payload: ExportCompanyDto) =>
  api.post(`/lead-finder/v2/companies/export`, payload);

export const getCompanyNamesAPI = async (
  companyPayload: any,
  activeSubTab: LeadFinderSecondaryTab,
) => {
  const queryParams = new URLSearchParams(window.location?.search);
  if (queryParams?.get('purgeCache') === '1') {
    companyPayload.purgeCache = true;
  }
  return api.post<ResponseSuccess>(
    `/lead-finder/v2/companies/names?tab=${activeSubTab}`,
    companyPayload,
  );
};

export const companyTagActionAPI = async (payload: CompanyTagActionPayload) =>
  api.post<ResponseSuccess>(`/lead-finder/v2/tag/company-action`, payload);

export const companyBulkTagActionAPI = async (
  payload: CompanyBulkTagActionPayload,
) =>
  api.post<ResponseSuccess>(`/lead-finder/v2/tag/company-bulk-action`, payload);

export const getCompanyNameDomainAPI = async (payload: {
  searchText: string;
}) =>
  api.get<ResponseSuccess>(
    `/lead-finder/v2/companies/autocomplete/name-domain`,
    {
      params: { searchText: encodeURIComponent(payload.searchText) },
    },
  );
export const getCompanyNameByIdsAPI = async (payload: {
  companyIds: number[];
}) =>
  api.post<ResponseSuccess>(`/lead-finder/v2/companies/names-by-ids`, payload);

export const getSignalsAPI = async () =>
  api.get<ResponseSuccess>(`/lead-finder/v2/companies/signals`);

export const getNewsAPI = async () =>
  api.get<ResponseSuccess>(`/lead-finder/v2/companies/news`);
